import type { LucideIcon } from 'lucide-react';
import { Icons } from '@/src/modules/components/Icons';

/** A single navigation entry — either a leaf (`path`) or a group (`children`). */
export type NavItem = {
  /** Stable identifier — also used as the React key. */
  id: string;
  label: string;
  icon: LucideIcon;
  /** Target route for leaf items. */
  path?: string;
  /** Exact-match flag passed to <NavLink end>. */
  end?: boolean;
  /** Optional pill rendered after the label (e.g. "BETA"). */
  badge?: string;
  /** Roles allowed to see this item. Omit to show it for every role. */
  roles?: string[];
  /**
   * RBAC permission code required to see this item (Phase 4).
   * Checked against the live permissions array from /auth/me.
   * Takes precedence over `roles` when both are set.
   */
  permission?: string;
  /** Nested entries — renders the item as a collapsible group. */
  children?: NavItem[];
};

/** A visually grouped block of items with an optional heading. */
export type NavSection = {
  title?: string;
  items: NavItem[];
};

/**
 * Dashboard navigation — the single source of truth for the sidebar.
 * Add/remove entries here; no component changes required.
 *
 * Permission-gated items use the `permission` field (requires the named
 * RBAC permission from /auth/me). Role-gated items use `roles` (legacy).
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: Icons.Dashboard, path: '/dashboard', end: true },
      { id: 'ai-invitations', label: 'AI Invitations', icon: Icons.Sparkles, path: '/dashboard/ai-invitations', badge: 'AI' },
      { id: 'invitations', label: 'Invitations', icon: Icons.History, path: '/dashboard/invitations' },
      { id: 'cart', label: 'Cart', icon: Icons.Cart, path: '/cart' },
      { id: 'calendar', label: 'Calendar', icon: Icons.Calendar, path: '/dashboard/calendar' },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: Icons.Analytics,
        path: '/dashboard/analytics',
        permission: 'VIEW_ANALYTICS',
      },
    ],
  },
  {
    title: 'Communication',
    items: [
      { id: 'chat', label: 'Chat', icon: Icons.Chat, path: '/dashboard/chat' },
      { id: 'notifications', label: 'Notifications', icon: Icons.Bell, path: '/dashboard/notifications' },
      { id: 'feedback', label: 'Feedback', icon: Icons.Feedback, path: '/dashboard/feedback' },
    ],
  },
  {
    title: 'Account',
    items: [
      { id: 'subscription', label: 'Billing', icon: Icons.Subscription, path: '/dashboard/subscription' },
      { id: 'settings', label: 'Settings', icon: Icons.Settings, path: '/dashboard/settings' },
      { id: 'help', label: 'Help & Support', icon: Icons.Help, path: '/dashboard/help' },
    ],
  },
];

/**
 * Filters a nav tree against the current user role. Items without a `roles`
 * list are visible to everyone; groups left empty after filtering are dropped.
 *
 * @deprecated Prefer filterNavByPermissions for new permission-gated items.
 * This function is kept for backward compatibility with legacy role checks.
 */
export function filterNavByRole(items: NavItem[], role: string): NavItem[] {
  return items
    .filter((item) => !item.roles || item.roles.includes(role))
    .map((item) =>
      item.children
        ? { ...item, children: filterNavByRole(item.children, role) }
        : item,
    )
    .filter((item) => item.path || (item.children?.length ?? 0) > 0);
}

/**
 * Filters a nav tree against the live RBAC permissions array (Phase 4).
 * Items without a `permission` field pass through unchanged.
 * Items with a `permission` field are hidden unless that code is present.
 */
export function filterNavByPermissions(items: NavItem[], permissions: string[]): NavItem[] {
  return items
    .filter((item) => !item.permission || permissions.includes(item.permission))
    .map((item) =>
      item.children
        ? { ...item, children: filterNavByPermissions(item.children, permissions) }
        : item,
    )
    .filter((item) => item.path || (item.children?.length ?? 0) > 0);
}
